import { observer } from "mobx-react"
import DataTable from "../../../components/DataTable/DataTable"
import TABLE_COLUMNS from "../const/TABLE_COLUMNS"
import { useContainerInjection } from "../../../hooks/useContainerInjection"
import DataTableRowsSkeleton from "../../../styledComponents/LoadingSkeletons/DataTableRowsSkeleton"
import { Checkbox, Tooltip } from "antd"
import { useTranslation } from "react-i18next"
import { BillingViewModel, InvoiceAggregate } from "../BillingViewModel"
import { Link } from "react-router-dom"
import { Invoice } from "../../../../domain/entities/Invoice"
import dayjs from "dayjs"
import { EyeOutlined } from "@ant-design/icons"
import { AuthStore } from "../../../stores/AuthStore"
import { RoutePaths } from "../../../navigation/RoutePaths.enum"
import { FormField } from "../../../components/Form/FormField"

interface InvoiceTableProps {
	data: (Invoice | InvoiceAggregate)[]
	loading?: boolean
	isFetching?: boolean
	onChangePage?: (page: number, pageSize: number, lastPageFetched: number) => void
}

const InvoiceTable = ({ data, loading, isFetching, onChangePage }: InvoiceTableProps) => {
	const viewModel = useContainerInjection<BillingViewModel>("BillingViewModel")
	const handlePageChange = (page: number, pageSize: number, lastPageFetched: number) => {
		viewModel.setTableConfig({ pageSize })
		onChangePage && onChangePage(page, pageSize, lastPageFetched)
	}
	const { isAdmin } = useContainerInjection<AuthStore>("AuthStore")
	const { t } = useTranslation("billing")

	const handleSave = (record: any) => {
		viewModel.updateInvoiceNumber(
			record.key.id ? [record.key.id] : (record.key as InvoiceAggregate).invoices.map(i => i.id),
			record.invoiceNumber
		)
	}
	return (
		<DataTable
			loading={isFetching}
			tableConfig={{
				sort: viewModel.tableConfig.sort,
				pageSize: viewModel.tableConfig.pageSize
			}}
			isFetching={isFetching}
			renderCustomLoadingComponent={() => <DataTableRowsSkeleton size="lg" />}
			onChange={data => {
				data.sorter &&
					viewModel.setTableConfig({ sort: { order: data.sorter.order, field: data.sorter.field } })
			}}
			onChangePage={handlePageChange}
			columns={TABLE_COLUMNS(value =>
				viewModel.setSelectedInvoiceIds(
					value ? data.flatMap(i => (i.id ? [i.id] : (i as InvoiceAggregate).invoices.map(i => i.id))) : []
				)
			)}
			editableCells={{ handleSave }}
			dataSource={data.map((invoice, idx) => {
				return {
					key: invoice,
					id: invoice.id ?? <b>{t("aggregated")}</b>,
					invoiceNumber: invoice.invoiceNumber,
					emissionDate: (
						<FormField.DatePicker
							className="mb-0"
							picker="date"
							value={invoice.emissionDate ? dayjs(invoice.emissionDate.split("T")[0]) : undefined}
							onChange={requestDate =>
								requestDate &&
								viewModel.updateInvoiceEmissionDate(
									invoice.id ? [invoice.id] : (invoice as InvoiceAggregate).invoices.map(i => i.id),
									requestDate.format("YYYY-MM-DDT[00:00:00Z]")
								)
							}
						/>
					),
					status: invoice.status ? t(invoice.status) : "-",
					period: invoice.period,
					sellerName: invoice.seller?.fiscalName ?? invoice.seller?.sellerName ?? "-",
					sellerOfficeName: invoice.id ? invoice.sellerOfficeName : t("multiple"),
					paymentDate: invoice.paymentDate ? dayjs(invoice.paymentDate).format("DD/MM/YYYY") : "-",
					paymentTerm: invoice.seller?.paymentDays ?? "-",
					total: invoice.total,
					totalFee: invoice.totalFee,
					sellerPrice: invoice.sellerPrice,
					transactions: invoice.transactions?.length ?? 0,
					checkbox: isAdmin && (
						<Checkbox
							key={invoice.id}
							checked={
								invoice.id
									? viewModel.selectedInvoiceIds.includes(invoice.id)
									: viewModel.selectedInvoiceIds.some(id =>
											(invoice as InvoiceAggregate).invoices.some(i => i.id === id)
									  )
							}
							onChange={value => {
								viewModel.setSelectedInvoiceIds(
									value.target.checked
										? viewModel.selectedInvoiceIds.concat(
												invoice.id
													? [invoice.id]
													: (invoice as InvoiceAggregate).invoices.map(i => i.id)
										  )
										: viewModel.selectedInvoiceIds.filter(id =>
												invoice.id
													? id !== invoice.id
													: (invoice as InvoiceAggregate).invoices.every(i => i.id !== id)
										  )
								)
							}}
						></Checkbox>
					),
					viewButton: (
						<div className="ant-employed">
							<Link
								to={RoutePaths.BILLING_TRANSACTIONS}
								onClick={() => viewModel.setSelectedInvoice(invoice)}
							>
								<Tooltip title={"View"}>
									<EyeOutlined />
								</Tooltip>
							</Link>
						</div>
					)
				}
			})}
		/>
	)
}

export default observer(InvoiceTable)
